/* eslint-disable max-len */
import { createSelector } from 'reselect';
import { INTERNAL_UNKNOWN, DATASET } from '@utils/constants';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import { combineWithMetricDeDuplication , getPercentageMetricByKey } from '@utils/metrics/metricHelpers';
import { DEFAULT_ML_METRIC_SORT } from '@utils/metricLens/mapping';
import { BASE_METRICS } from '@utils/metrics';
import { SCOPE } from './types';
import {
  allFiltersSelector,
  selectedMetricsSelector,
  selectedDimensionSelector,
  appliedSortSelector,
} from '../base/selectors';

const selector = (state) => get(state, SCOPE);

const metricLiveRequestBody = {
  queries: [
    {
      type: 'groupby',
      dataset: DATASET,
      metrics: [DEFAULT_ML_METRIC_SORT.metric],
      filter: {},
      limit: 200,
      groupBy: [],
      granularity: 'ALL',
      sortBy: [DEFAULT_ML_METRIC_SORT],
      options: {
        withTotals: true,
      },
    },
  ],
};

export const payloadSelector = createSelector(
  selectedMetricsSelector,
  allFiltersSelector,
  selectedDimensionSelector,
  appliedSortSelector,
  (metrics, filters, dimension) =>
    filters.map((eachFilter) => {
      const invertedMetrics = [];
      const metricsList = combineWithMetricDeDuplication(metrics, BASE_METRICS);
      metricsList.forEach((m) => {
        const percentageMetric = getPercentageMetricByKey({ metricKey: m });
        if (percentageMetric) {
          invertedMetrics.push(percentageMetric);
        }
      });
      const body = cloneDeep(metricLiveRequestBody);
      body.queries[0].limit = 200;
      body.queries[0].metrics = combineWithMetricDeDuplication(body.queries[0].metrics, [
        ...metricsList,
        ...invertedMetrics,
      ]);
      body.queries[0].filter = eachFilter.filter.rule;
      body.queries[0].groupBy = [dimension];
      return body;
    }),
);

export const liveDataSelector = createSelector(selector, (liveData) =>
  liveData.response ? liveData.response : [],
);

export const liveLoadSelector = createSelector(selector, (liveData) => ({
  loading: liveData.isFetching,
  errorCode: liveData.errorCode,
  errorMsg: liveData.errorMsg,
}));

export const liveMaxMetricsDataSelector = createSelector(
  liveDataSelector,
  selectedMetricsSelector,
  (tableData, metrics) => {
    const maxMetrics = {};
    const invertedMetrics = [];
    metrics.forEach((m) => {
      const percentageMetric = getPercentageMetricByKey({ metricKey: m });
      if (percentageMetric) {
        invertedMetrics.push(percentageMetric);
      }
    });
    const fullMetrics = [...metrics, ...invertedMetrics];
    tableData.forEach((eachData) => {
      get(eachData[0], 'data[0].results', []).forEach((row) => {
        fullMetrics.forEach((metric) => {
          if (maxMetrics[metric]) {
            if (row[metric] > maxMetrics[metric]) {
              maxMetrics[metric] = row[metric];
            }
          } else {
            maxMetrics[metric] = row[metric];
          }
        });
      });
    });
    return maxMetrics;
  },
);

const getUniqueDimensions = (data) => {
  let flattenedDimensionKeys = [];
  // get the complete set of dimensions names and set id
  const dimensionKeys = [];
  data.forEach((eachData) => {
    const newDimensionKeySet = [];
    eachData.forEach((row) => {
      const rowId = row.value.join('');
      // eslint-disable-next-line no-param-reassign
      row.id = rowId;
      newDimensionKeySet.push(rowId);
    });
    dimensionKeys.push(newDimensionKeySet);
  });
  // flatten the dimension name array list and create a set to remove duplicates and convert it back to array
  flattenedDimensionKeys = [...new Set(dimensionKeys.flat())];
  return flattenedDimensionKeys;
};

const setMissingValues = (data, metrics) => {
  // create empty metrics data
  const emptyMetrics = {};
  metrics.forEach((metric) => {
    emptyMetrics[metric] = 0;
  });

  const flattenedDimensionKeys = getUniqueDimensions(data);

  const mergedAllData = data.flat(Infinity);

  // loop through the each data and check whether the demsnion is present if not add empty metric data
  data.map((tableData) => {
    flattenedDimensionKeys.forEach((dimensionKey) => {
      const present = tableData.some((o) => o.id === dimensionKey);
      if (!present) {
        const found = mergedAllData.find((eachRow) => eachRow.id === dimensionKey);
        tableData.push({
          ...emptyMetrics,
          id: dimensionKey,
          value: [dimensionKey],
          name: [found && found.name ? found.name.join('') : INTERNAL_UNKNOWN],
        });
      }
    });
    return tableData;
  });
  return data;
};

export const liveRangeDataSelector = createSelector(
  liveDataSelector,
  selectedMetricsSelector,
  appliedSortSelector,
  (data, metrics, sort) => {
    let unknownRow = null;
    const records = [];
    const newTableData = cloneDeep(data);
    const cherryPickData = newTableData.map((eachFilterData) =>
      get(eachFilterData[0], 'data[0].results', []),
    );
    const invertedMetrics = [];
    metrics.forEach((m) => {
      const percentageMetric = getPercentageMetricByKey({ metricKey: m });
      if (percentageMetric) {
        invertedMetrics.push(percentageMetric);
      }
    });
    const prepareData = setMissingValues(cherryPickData, [...metrics, ...invertedMetrics]);
    if (prepareData[sort.filter]) {
      prepareData[sort.filter].forEach((row) => {
        // Unknown row
        if (row.id !== INTERNAL_UNKNOWN) {
          records.push(row);
        } else {
          unknownRow = row;
        }
      });
      // sort data
      const sortedData = orderBy(records, sort.metric, sort.order);
      if (unknownRow) {
        sortedData.push(unknownRow);
      }
      prepareData[sort.filter] = sortedData;
    }
    return prepareData;
  },
);

export const liveRangeTotalDataSelector = createSelector(liveDataSelector, (liveData) => {
  const totalData = [];
  liveData.forEach((eachData) => {
    const data = get(eachData[0], 'totals[0].results', {});
    totalData.push(data);
  });
  return totalData;
});

export const liveDataTimeSelector = createSelector(liveDataSelector, (liveData) => {
  const liveTimeArray = liveData.map((eachLiveData) =>
    get(eachLiveData, '[0]totals[0].timestamp', null),
  );
  const filteredArray = liveTimeArray.filter(Boolean);
  return filteredArray[0] || null;
});

export default selector;
